/**
 * useLoadingProgress Hook
 *
 * Custom React hook for managing loading progress simulation with dynamic text updates.
 * Provides fake progress updates that go from 1% to 90% over 2 minutes, with text changes
 * at specific intervals as specified in the requirements.
 *
 * @module hooks/useLoadingProgress
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Loading progress state interface
 */
export interface LoadingProgressState {
  /** Current progress percentage (0-100) */
  progress: number;
  /** Current loading text */
  loadingText: string;
  /** Whether loading is active */
  isLoading: boolean;
}

/**
 * Return type for useLoadingProgress hook
 */
export interface UseLoadingProgressReturn extends LoadingProgressState {
  /** Start the loading progress simulation */
  startLoading: () => void;
  /** Stop the loading progress and reset */
  stopLoading: () => void;
  /** Complete the loading (show 100% for 2 seconds then hide) */
  completeLoading: () => Promise<void>;
}

/**
 * Hook for managing loading progress simulation
 *
 * Features:
 * - Progress goes from 1% to 90% over 2 minutes (120 seconds)
 * - Text changes: "Loading..." -> "Still working on it..." (60s) -> "This is taking a bit longer..." (120s)
 * - Shows 100% for 2 seconds after completion before hiding
 *
 * @returns Object containing progress state and control functions
 *
 * @example
 * ```typescript
 * const LoadingComponent = () => {
 *   const { progress, loadingText, isLoading, startLoading, completeLoading } = useLoadingProgress();
 *
 *   const handleStart = async () => {
 *     startLoading();
 *     try {
 *       await someApiCall();
 *       await completeLoading();
 *     } catch (error) {
 *       stopLoading();
 *     }
 *   };
 *
 *   return (
 *     <LoadingDialog
 *       isOpen={isLoading}
 *       title={loadingText}
 *       message="Processing your request..."
 *       estimatedTimeSeconds={30}
 *       isComplete={progress === 100}
 *     />
 *   );
 * };
 * ```
 */
export const useLoadingProgress = (): UseLoadingProgressReturn => {
  const [progress, setProgress] = useState<number>(0);
  const [loadingText, setLoadingText] = useState<string>('Loading...');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Refs to track intervals and timeouts
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const textTimeout1Ref = useRef<NodeJS.Timeout | null>(null);
  const textTimeout2Ref = useRef<NodeJS.Timeout | null>(null);

  /**
   * Clear all timers and intervals
   */
  const clearTimers = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
    if (textTimeout1Ref.current) {
      clearTimeout(textTimeout1Ref.current);
      textTimeout1Ref.current = null;
    }
    if (textTimeout2Ref.current) {
      clearTimeout(textTimeout2Ref.current);
      textTimeout2Ref.current = null;
    }
  }, []);

  /**
   * Start the loading progress simulation
   */
  const startLoading = useCallback(() => {
    // Clear any existing timers
    clearTimers();

    // Reset state
    setProgress(1);
    setLoadingText('Loading...');
    setIsLoading(true);

    // Progress simulation: 1% to 90% over 120 seconds (2 minutes)
    // Update every 500ms for smooth animation
    // Total progress range: 89% (90% - 1%)
    // Total time: 120,000ms
    // Updates: 120,000ms / 500ms = 240 updates
    // Progress per update: 89% / 240 = ~0.371%
    const progressIncrement = 89 / 240;
    let currentProgress = 1;

    progressIntervalRef.current = setInterval(() => {
      currentProgress += progressIncrement;
      if (currentProgress >= 90) {
        currentProgress = 90;
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
      }
      setProgress(currentProgress);
    }, 500);

    // Text change after 60 seconds
    textTimeout1Ref.current = setTimeout(() => {
      setLoadingText('Still working on it...');
    }, 60000);

    // Text change after 120 seconds (2 minutes)
    textTimeout2Ref.current = setTimeout(() => {
      setLoadingText('This is taking a bit longer than expected. Please wait some more...');
    }, 120000);
  }, [clearTimers]);

  /**
   * Stop the loading progress and reset
   */
  const stopLoading = useCallback(() => {
    clearTimers();
    setProgress(0);
    setLoadingText('Loading...');
    setIsLoading(false);
  }, [clearTimers]);

  /**
   * Complete the loading (show 100% for 2 seconds then hide)
   */
  const completeLoading = useCallback(async (): Promise<void> => {
    return new Promise((resolve) => {
      // Clear existing timers
      clearTimers();

      // Set to 100% and update text
      setProgress(100);
      setLoadingText('Complete!');

      // Hide after 2 seconds
      setTimeout(() => {
        setIsLoading(false);
        setProgress(0);
        setLoadingText('Loading...');
        resolve();
      }, 2000);
    });
  }, [clearTimers]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearTimers();
    };
  }, [clearTimers]);

  return {
    progress,
    loadingText,
    isLoading,
    startLoading,
    stopLoading,
    completeLoading,
  };
};

export default useLoadingProgress;